import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, obj = logged in

  const loadMe = useCallback(async () => {
    // Cookie-only auth: the httpOnly session cookie is sent automatically.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  const googleSession = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sessionId } });
    setUser(data.user);
    return data.user;
  }, []);

  useEffect(() => {
    // Emergent Google OAuth returns to /adaptive#session_id=... — process it before the /me check.
    if (window.location.hash.includes("session_id=")) {
      const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
      window.history.replaceState(null, "", window.location.pathname);
      if (sid) {
        googleSession(sid).catch(() => { setUser(false); toast.error("Google sign-in could not be completed. Please try again."); });
        return;
      }
    }
    loadMe();
  }, [loadMe, googleSession]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { void err; /* clear session cookie server-side */ }
    setUser(false);
  };

  const refreshUser = loadMe;

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshUser, googleSession, formatApiErrorDetail }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
